import { PublicClientApplication } from '@azure/msal-browser'

/**
 * Idle timeout configuration
 * Automatically logs out users after 30 minutes of inactivity across ALL tabs
 * This provides security while allowing cross-tab SSO with localStorage
 */
const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes in milliseconds
const ACTIVITY_KEY = 'cih.lastActivity' // localStorage key for cross-tab activity tracking
let idleTimer: NodeJS.Timeout | null = null
let isSetup = false // Prevent multiple initializations

/**
 * Updates the last activity timestamp in localStorage
 * This broadcasts activity to all open tabs
 */
const updateLastActivity = (): void => {
  const timestamp = Date.now().toString()
  localStorage.setItem(ACTIVITY_KEY, timestamp)
}

/**
 * Gets the last activity timestamp from localStorage
 */
const getLastActivity = (): number => {
  const stored = localStorage.getItem(ACTIVITY_KEY)
  return stored !== null ? Number.parseInt(stored, 10) : Date.now()
}

/**
 * Sets up idle timeout monitoring
 * Logs out the user after specified period of inactivity across ALL tabs
 *
 * @param msalInstance - The MSAL instance for handling logout
 */
export const setupIdleTimeout = (msalInstance: PublicClientApplication): void => {
  // Skip idle timeout in development if auth is bypassed
  if (process.env.NODE_ENV === 'development' && process.env.SKIP_AUTHENTICATION === 'true') {
    return
  }

  // Prevent multiple initializations
  if (isSetup) {
    console.log('Idle timeout already initialized')
    return
  }

  isSetup = true
  console.log('Setting up cross-tab idle timeout (30 minutes)')

  // Initialize last activity timestamp
  updateLastActivity()

  const checkIdleTimeout = (): void => {
    const lastActivity = getLastActivity()
    const timeSinceActivity = Date.now() - lastActivity

    if (timeSinceActivity >= IDLE_TIMEOUT) {
      console.warn('User idle timeout reached across all tabs - logging out')

      // Clear MSAL cache before logout
      const accounts = msalInstance.getAllAccounts()
      accounts.forEach(account => {
        msalInstance.setActiveAccount(null)
      })

      // Remove activity tracking
      localStorage.removeItem(ACTIVITY_KEY)

      // Logout and redirect
      msalInstance.logoutRedirect({
        postLogoutRedirectUri: '/'
      }).catch((error) => {
        console.error('Idle logout error:', error)
      })
    } else {
      // Not idle yet, check again in 1 minute
      idleTimer = setTimeout(checkIdleTimeout, 60 * 1000)
    }
  }

  /**
   * Handles user activity - updates timestamp and resets check timer
   */
  const handleActivity = (): void => {
    updateLastActivity()

    // Reset the check timer
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }
    idleTimer = setTimeout(checkIdleTimeout, 60 * 1000) // Check every minute
  }

  // Monitor user activity across various interactions
  const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click']

  events.forEach(event => {
    window.addEventListener(event, handleActivity, true)
  })

  // Listen for activity in other tabs via localStorage changes
  const handleStorageChange = (event: StorageEvent): void => {
    if (event.key === ACTIVITY_KEY && event.newValue !== null) {
      // Another tab had activity, reset our check timer
      if (idleTimer !== null) {
        clearTimeout(idleTimer)
      }
      idleTimer = setTimeout(checkIdleTimeout, 60 * 1000)
    }
  }

  window.addEventListener('storage', handleStorageChange)

  // Start the initial check timer
  idleTimer = setTimeout(checkIdleTimeout, 60 * 1000)

  // Cleanup function (optional, for component unmount)
  return () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }
    events.forEach(event => {
      window.removeEventListener(event, handleActivity, true)
    })
    window.removeEventListener('storage', handleStorageChange)
  }
}
